import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { listCollaborationWorkspaces, deleteCollaborationWorkspace } from '../../api/backendApi';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';

const CollaborationWorkspaceListPage = () => {
  const navigate = useNavigate();
  const { userData, tenantId: authTenantId } = useAuth();

  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tenantId = authTenantId || userData?.tenantId;
  const role = userData?.role;
  const hasAdminAccess = ['admin', 'superadmin', 'tenant_admin', 'overseer'].includes(role);

  const loadWorkspaces = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    if (!hasAdminAccess) { setError('Admin access required'); setLoading(false); return; }
    try {
      const items = await listCollaborationWorkspaces(tenantId);
      setWorkspaces(items);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load collaboration workspaces');
    } finally {
      setLoading(false);
    }
  }, [tenantId, hasAdminAccess]);

  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete collaboration workspace "${name}"?`)) return;
    try {
      await deleteCollaborationWorkspace(tenantId, id);
      loadWorkspaces();
    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    {
      field: 'participants',
      headerName: 'Participants',
      width: 120,
      valueGetter: (params) => {
        const row = params?.row || params;
        const p = row?.participants;
        return Array.isArray(p) ? p.length : 0;
      }
    },
    {
      field: 'googleMeetUrl',
      headerName: 'Links',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const count = [row.googleMeetUrl, row.googleCalendarUrl, row.googleSlidesUrl, row.googleDriveUrl]
          .filter(Boolean).length;
        return count > 0 ? <Chip label={count} size="small" color="info" /> : '—';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(`/admin/collaboration-workspaces/${params.row.id}`)} title="Edit">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row.id, params.row.name)} color="error" title="Delete">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  if (!hasAdminAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Admin access required.</Alert>
      </Box>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Collaboration Workspaces
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Shared workspaces with Google links, participants, and notes for team collaboration
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/collaboration-workspaces/new')}
          >
            Create Workspace
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <DataGrid
              rows={workspaces}
              columns={columns}
              loading={loading}
              autoHeight
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' }
              }}
            />
          </CardContent>
        </Card>

        {!loading && workspaces.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No collaboration workspaces yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a workspace to share Google Meet, Calendar, Slides, and Drive links with your team
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/collaboration-workspaces/new')}>
              Create Workspace
            </Button>
          </Box>
        )}
      </Box>
    </MainLayout>
  );
};

export default CollaborationWorkspaceListPage;
